/* eslint-disable */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Skeleton,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState } from "react";
import getTheUserState from "../../../../utils/userState";
import { useQuery } from "@tanstack/react-query";
import { companyServices } from "../../../../services/company";
import { useFormContext } from "react-hook-form";
import LoaderSkeletonComponent from "./LoadingSkeletonComponent";
import { PermissionsSchema } from "../../../../components/auth/PermissionsWrapper";

const FileEditMultipleCompanies = () => {
  const userCompanies = getTheUserState()?.user?.companies;
  const { setValue, watch } = useFormContext();
  const defaultCompanies = watch("companies");

  const loggedUser = getTheUserState();

  const checkCompaniesPermission = loggedUser?.user?.role?.permissions?.some((permission) => {
    return (
      permission.permission_name === PermissionsSchema?.companies?.view_all_companies &&
      permission.status === true
    );
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [defaultCompaniesValues, setDefaultCompaniesValues] = useState([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (isDrawerOpen) {
      setDefaultCompaniesValues(defaultCompanies);
    }
  }, [isDrawerOpen]);

  const {
    data: companiesData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["companiesByUserId", userCompanies],
    queryFn: checkCompaniesPermission
      ? () => companyServices.getAllCompaniesAndProjectsQuery()
      : () => companyServices.getCompaniesAndProjectsQuery(userCompanies),
    enabled: isDrawerOpen,
  });

  function handleDrawerState() {
    setIsDrawerOpen(!isDrawerOpen);
  }

  const filteredData = companiesData?.data?.data?.filter((item) => {
    const matchSearch = item?.name.toLowerCase()?.includes(searchText);
    const hasProjects = Array.isArray(item?.projects) && item?.projects.length > 0;

    return matchSearch && hasProjects;
  });

  // Handle Search functionality
  function onHandleSearch(e) {
    const searchValue = e.target.value;
    setSearchText(searchValue.toLowerCase());
  }

  // Functionality to handle All Projects in a Company
  function handleCheckAll(e, comp) {
    const isChecked = e.target.checked;

    const companyID = comp?.id;
    const doCompanyExists = defaultCompaniesValues.some((obj) => obj?.company_id === companyID);

    if (isChecked) {
      const updateSettings = [...defaultCompaniesValues];

      if (doCompanyExists) {
        const settingsToUpdate = updateSettings.map((obj) => {
          if (obj.company_id === companyID) {
            const projects = [];

            comp?.projects?.forEach((prj) => {
              projects.push(prj?.id);
            });
            return { ...obj, projects: projects };
          }
          return obj;
        });

        setDefaultCompaniesValues(settingsToUpdate);
      } else {
        const selectedProject = comp?.projects;

        const projects = [];

        selectedProject?.forEach((prj) => {
          projects.push(prj?.id);
        });

        setDefaultCompaniesValues((prev) => [
          ...prev,
          {
            company_id: companyID,
            projects: projects,
          },
        ]);
      }
    } else {
      if (doCompanyExists) {
        const removeObject = defaultCompaniesValues.filter((s) => s.company_id !== companyID);
        setDefaultCompaniesValues(removeObject);
      }
    }
  }

  // Functionality to handle Project in a Company
  function handleAdjustSettings(event, comp, index) {
    const isChecked = event.target.checked;

    const updateSettings = [...defaultCompaniesValues];

    const companyID = comp?.id;
    const doCompanyExists = defaultCompaniesValues.some((obj) => obj?.company_id === companyID);

    if (isChecked) {
      if (doCompanyExists) {
        const settingsToUpdate = updateSettings.map((obj) => {
          if (obj.company_id === companyID) {
            const updatedProjects = [...obj.projects, comp?.projects[index]?.id];

            return { ...obj, projects: updatedProjects };
          }
          return obj;
        });

        setDefaultCompaniesValues(settingsToUpdate);
      } else {
        const selectedProject = comp?.projects[index];

        setDefaultCompaniesValues((prev) => [
          ...prev,
          {
            company_id: companyID,
            projects: [selectedProject?.id],
          },
        ]);
      }
    } else {
      if (doCompanyExists) {
        const settingsToUpdate = updateSettings.map((obj) => {
          if (obj.company_id === companyID) {
            const updatedProjects = obj.projects.filter(
              (project) => project !== comp?.projects[index]?.id,
            );

            if (updatedProjects.length === 0) {
              return null; // Returning null will filter out the object from the array
            }

            return { ...obj, projects: updatedProjects };
          }
          return obj;
        });
        const filteredArray = settingsToUpdate.filter((obj) => obj !== null);

        setDefaultCompaniesValues(filteredArray);
      }
    }
  }

  // Save the selected Settings
  function handleAssignSelectedSettings() {
    setValue("companies", defaultCompaniesValues, { shouldDirty: true });
    setValue("is_private", false);
    setValue("is_general", false);
    setValue("file_user", []);
    setIsDrawerOpen(false);
    setDefaultCompaniesValues([]);
  }

  let buttonTitle = defaultCompanies?.length ? "Assigned projects" : "Assign projects";
  const hasCompaniesAssigned = defaultCompanies?.length > 0;

  return (
    <div>
      <div onClick={handleDrawerState} style={{ cursor: "pointer" }}>
        <Button
          variant="outlined"
          color={hasCompaniesAssigned ? "success" : "info"}
          sx={{ textTransform: "capitalize", fontSize: "11px" }}
        >
          {buttonTitle}
        </Button>
      </div>
      <Drawer
        onClose={handleDrawerState}
        anchor="right"
        open={isDrawerOpen}
        sx={selectCompanyStyles.drawerRoot}
      >
        <div
          style={{
            padding: "48px 32px",
            minWidth: "324px",
            // maxHeight: "550px",
            height: "100%",
            minHeight: "550px",
            overflowY: "auto",
          }}
        >
          <p style={selectCompanyStyles.title}>Choose Companies</p>
          <p style={selectCompanyStyles.smallTitle}>
            Only companies with projects are displayed here
          </p>
          <TextField
            id="standard-basic"
            placeholder="Type to search..."
            variant="standard"
            onChange={onHandleSearch}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {isLoading ? (
            <LoaderSkeletonComponent count={12} />
          ) : filteredData?.length ? (
            filteredData?.map((comp, companyIndex) => {
              const projectsInCompany = defaultCompaniesValues.filter(
                (s) => s.company_id === comp?.id,
              );
              const projectsLength = projectsInCompany[0]?.projects?.length;
              return (
                <Accordion
                  // expanded={isOpen}
                  key={comp?.id}
                  sx={{
                    boxShadow: "none",
                    // borderBottom: "1px solid #E8E8E9",
                    borderRadius: "0px",
                    cursor: comp?.projects?.length ? "pointer" : "not-allowed",
                    "& .MuiButtonBase-root": {
                      padding: "0px 0px",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id={`panel1-header-${companyIndex}`}
                    sx={{
                      "& .Mui-expanded": {
                        margin: "10px 0px",
                      },
                    }}
                  >
                    <div>
                      <Checkbox
                        onClick={(e) => {
                          handleCheckAll(e, comp);
                          e.stopPropagation();
                        }}
                        checked={comp?.projects?.length === projectsLength}
                      />
                      <Badge badgeContent={projectsLength} color="primary">
                        <span style={selectCompanyStyles.accordionLabels}> {comp?.name}</span>
                      </Badge>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: "5px 0px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        borderBottom: "1px solid #F4F4F4",
                      }}
                    ></div>
                    <FormGroup>
                      {comp?.projects?.map((proj, index) => {
                        const filterData = defaultCompaniesValues?.filter(
                          (setting) => setting?.company_id === comp?.id,
                        );

                        const isChecked = filterData[0]?.projects.includes(proj?.id);

                        return (
                          <div
                            style={{
                              width: "100%",
                              borderBottom: "1px solid #F4F4F4",
                              padding: "8px 24px",
                              // marginLeft: "24px",
                            }}
                            key={index}
                          >
                            <FormControlLabel
                              key={index}
                              control={
                                <Checkbox
                                  onClick={(e) => handleAdjustSettings(e, comp, index)}
                                  checked={isChecked ? true : false}
                                />
                              }
                              label={proj?.name}
                              sx={{
                                // styles for label text
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "14px",
                                  marginLeft: "15px",
                                },
                              }}
                            />
                          </div>
                        );
                      })}
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            <p
              style={{
                fontFamily: "IBM Plex Sans",
                fontWeight: "600",
                fontSize: "23px",
                lineHeight: "32.2px",
                letterSpace: "0.20px",
                color: "#008de4",
                textAlign: "center",
                minWidth: "360px",
              }}
            >
              No matches!
            </p>
          )}
        </div>
        <div style={{ padding: "12px 32px", borderTop: "1px solid  #E8E8E9" }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="text"
              onClick={handleDrawerState}
              sx={selectCompanyStyles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant="text"
              onClick={handleAssignSelectedSettings}
              sx={true ? selectCompanyStyles.addButton : selectCompanyStyles.addButtonDisabled}
              // disabled={true}
            >
              Add
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default FileEditMultipleCompanies;

const selectCompanyStyles = {
  iconStyle: {
    color: "#1E9DF2",
  },
  drawerRoot: {
    "& .MuiPaper-root": { minWidth: "360px", overflow: "hidden" },
    "& .MuiBackdrop-root": { background: "transparent" },
  },
  formLabel: { "& .MuiTypography-root": { fontSize: "11px" } },
  buttons: {
    textTransform: "Capitalize",
  },
  title: {
    fontFamily: "IBM Plex Sans",
    fontWeight: "600",
    fontSize: "23px",
    lineHeight: "32.2px",
    letterSpace: "0.20px",
    margin: "0.5em 0px",
  },
  smallTitle: {
    fontFamily: "IBM Plex Sans",
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "18.8px",
    letterSpace: "0.20px",
    color: "#D22B2B",
  },
  accordionLabels: {
    fontFamily: "IBM Plex Sans",
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "18.8px",
    letterSpace: "0.20px",
    paddingLeft: "15px",
  },
  addButton: {
    borderRadius: "2px",
    background: "#1E9DF2",
    fontFamily: "IBM Plex Sans",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "22.4px",
    letterSpacing: "-0.32px",
    textTransform: "capitalize",
    width: "120px",
    height: "40px",
    color: "white",
    "&:hover": {
      background: "#1E9DF2",
    },
  },
  addButtonDisabled: {
    borderRadius: "2px",
    background: "#D1D2D2",
    fontFamily: "IBM Plex Sans",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "22.4px",
    letterSpacing: "-0.32px",
    textTransform: "capitalize",
    width: "120px",
    height: "40px",
    color: "white!important",
    "&:hover": {
      background: "#D1D2D2",
    },
  },
  cancelButton: {
    borderRadius: "2px",
    background: "#FFF",
    fontFamily: "IBM Plex Sans",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "22.4px",
    letterSpacing: "-0.32px",
    textTransform: "capitalize",
    width: "120px",
    height: "40px",
    color: "#1E9DF2",
    marginRight: "8px",
  },
};
